import { computed, Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import { CompaniesResponse, getAllCompanies } from '@cognitivecredit/ui-utils'
import { AxiosResponse } from 'axios'

// This store contains (for now) only the list of all companies as
// returned by the getAllCompanies API call, which returns only
// id, name, ticker, and licenseIds for each company.
// This is used in various places in the app where we need to map
// company IDs to names or tickers without fetching full company details.

export const useCompaniesStore = defineStore('companies', () => {
  const companiesLoaded = ref(false)
  const loadingCompanies: Ref<Promise<AxiosResponse<Record<string, CompaniesResponse>, any>> | null> = ref(null)
  const companies: Ref<Record<string, CompaniesResponse>> = ref({})
  const companiesWithDealConnectV2Deals: Ref<string[]> = ref([])
  const clientHasDealConnectV2Deals: Ref<boolean> = ref(false)
  const dealConnectV2DealsLoaded = ref(false)
  const loadingDealConnectV2Deals: Ref<Promise<boolean> | null> = ref(null)

  const loadCompanies = async () => {
    try {
      // loadCompanies may be called multiple times in quick succession (e.g. if the user navigates to DealConnect, this will also be called in App.vue and the beforeEach hook), so we want to avoid making multiple API calls. If a call is already in progress, we can just await it.
      if (!loadingCompanies.value) {
        loadingCompanies.value = getAllCompanies() as Promise<AxiosResponse<Record<string, CompaniesResponse>, any>>
      }
      const res = await loadingCompanies.value
      companies.value = res!.data
      companiesLoaded.value = true
    } catch (e) {
      console.error('`[ERROR] Loading companies failed:', e)
    } finally {
      loadingCompanies.value = null
    }
    return companies.value
  }

  const setDealConnectV2Deals = (companyIds: string[], clientHasDeals = companyIds.length > 0) => {
    companiesWithDealConnectV2Deals.value = companyIds
    clientHasDealConnectV2Deals.value = clientHasDeals
    dealConnectV2DealsLoaded.value = true
  }

  const getCompanyById = (companyId: string) => {
    if (!companiesLoaded.value) {
      throw new Error('Companies not loaded yet. Call loadCompanies() first.')
    }
    return companies.value[companyId]
  }
  const companyHasDealDocs = (companyId: string) => {
    getCompanyById(companyId)
    return companiesWithDealConnectV2Deals.value.includes(companyId)
  }
  const clientHasAnyDealDocs = computed(() => {
    if (!companiesLoaded.value) {
      return false
    }
    return clientHasDealConnectV2Deals.value
  })
  return {
    loadCompanies,
    companiesLoaded,
    loadingCompanies,
    companies,
    getCompanyById,
    companyHasDealDocs,
    clientHasAnyDealDocs,
    clientHasDealConnectV2Deals,
    companiesWithDealConnectV2Deals,
    dealConnectV2DealsLoaded,
    loadingDealConnectV2Deals,
    setDealConnectV2Deals,
  }
})
